import {
  Button,
  buttonVariants,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@folio/components';
import { ErrorMessage } from '@folio/components/module-elements/ErrorMessage';
import { clearMfaSetupSkip } from '@folio/components/features/auth/mfaSetupSkip';
import { cn, formatDate } from '@folio/lib';
import { KorintConfigError } from '@folio/lib/config-error';
import { takeOAuthRedirect } from '@folio/utils/oauth-redirect';
import { getRedirectUrl, readsDistributionRequests } from '@folio/utils/redirect-utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  customerControllerGetCustomerQueryOptions,
  type DistributionRequestDto,
  LegacyAtMeOutput,
  legacyPermissionsControllerAtMeQueryOptions,
  policyControllerGetPolicyQueryOptions,
  viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions,
  viewSpecificControllerListPolicyAssetsQueryOptions,
} from 'korint-sdk';
import { useKorintModuleConfig, useKorintProductConfig, useSignOut } from 'korint-sdk/react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { useEffect } from 'react';
import { ChevronRightIcon, Loader, LogOut } from 'lucide-react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { mutate } from 'swr';
import { Center, Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

interface RoleRowProps {
  resourceId: string;
  relationType: string;
  isUniqueRow: boolean;
  userProducts?: string[];
  distributionRequests?: DistributionRequestDto[];
}

interface RedirectModuleProps {
  logo?: React.ReactNode;
}

// Disgusting way to clear the cache. https://swr.vercel.app/docs/mutation.en-US#mutate-multiple-items
function clearCache() {
  return mutate(() => true, undefined, { revalidate: false });
}

function RoleRow({
  resourceId,
  relationType,
  isUniqueRow,
  userProducts,
  distributionRequests,
}: RoleRowProps) {
  const { t } = useTranslation();
  const businessRolesConfig = useKorintModuleConfig('businessRolesConfig');
  const {
    productsConfig,
    isLoading: isProductConfigLoading,
    isError: isProductConfigError,
  } = useKorintProductConfig();

  const enableGetBrokerageFirm = resourceId.startsWith('brokerage_firm_');
  const { data: brokerageFirm, status: getBrokerageFirmStatus } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(resourceId),
    enabled: enableGetBrokerageFirm,
  });

  const enableGetCustomer = resourceId.startsWith('customer_');
  const { data: customer, status: getCustomerStatus } = useQuery({
    ...customerControllerGetCustomerQueryOptions(resourceId),
    enabled: enableGetCustomer,
  });

  const enableGetPolicy = !!customer?.policies[0];
  const { data: policy, status: getPolicyStatus } = useQuery({
    ...policyControllerGetPolicyQueryOptions(customer?.policies[0] ?? ''),
    enabled: enableGetPolicy,
  });

  const product = policy?.productId ? productsConfig[policy.productId] : undefined;

  const enableGetAssets = !!policy && !!product?.redirectDisplayName;
  const { data: assetsResponse, status: getAssetsStatus } = useQuery({
    ...viewSpecificControllerListPolicyAssetsQueryOptions(policy?.id ?? ''),
    enabled: enableGetAssets,
  });

  if (
    isProductConfigLoading ||
    (enableGetBrokerageFirm && getBrokerageFirmStatus === 'pending') ||
    (enableGetCustomer && getCustomerStatus === 'pending') ||
    (enableGetPolicy && getPolicyStatus === 'pending') ||
    (enableGetAssets && getAssetsStatus === 'pending')
  ) {
    return <Loader className="size-5 animate-spin self-center" />;
  }

  if (
    isProductConfigError ||
    (enableGetBrokerageFirm && getBrokerageFirmStatus === 'error') ||
    (enableGetCustomer && getCustomerStatus === 'error') ||
    (enableGetPolicy && getPolicyStatus === 'error') ||
    (enableGetAssets && getAssetsStatus === 'error')
  ) {
    return <ErrorMessage />;
  }

  const businessRoleConfig = businessRolesConfig?.[relationType];
  if (!businessRoleConfig) {
    throw new KorintConfigError(`No business role config found for ${relationType}`);
  }
  if (policy?.productId && !product) {
    throw new KorintConfigError(`No product config found for ${policy?.productId}`);
  }

  const redirectTo = getRedirectUrl(businessRoleConfig.redirectRules, {
    userProducts: userProducts ?? [],
    distributionRequests: distributionRequests ?? [],
    brokerageFirmId: brokerageFirm?.id ?? '',
    policyId: policy?.id ?? '',
    customerId: customer?.id ?? '',
  });

  if (isUniqueRow) {
    return <Navigate to={redirectTo} />;
  }

  const brokerageFirmDisplay = !!brokerageFirm && _.get(brokerageFirm, ['companyInfo', 'name']);
  const policyRedirectDisplayName =
    !!policy &&
    !!customer &&
    product?.redirectDisplayName &&
    product.redirectDisplayName({
      assets: assetsResponse?.data ?? [],
      policy,
      customer,
    });
  const productDisplay = !!policy && policy.productId && t(`products.${policy.productId}.label`);
  const policyStartedAtDisplay =
    !!policy?.startedAt &&
    !product?.redirectDisplayName &&
    t('redirect.contractStart', { date: formatDate(new Date(policy.startedAt)) });
  const display = [
    brokerageFirmDisplay,
    policyRedirectDisplayName,
    productDisplay,
    policyStartedAtDisplay,
  ]
    .filter(Boolean)
    .join(' - ');

  return (
    <Link
      to={redirectTo}
      className={cn(buttonVariants({ variant: 'ghost' }), 'justify-start w-full h-auto p-4')}
    >
      <Group justify="between" gap={0} className="w-full">
        <div className="grid gap-1 justify-items-start">
          <div className="text-lg">{t(`redirect.businessRoles.${relationType}`)}</div>
          <div className="text-sm text-muted-foreground gap-2">{display}</div>
        </div>
        <ChevronRightIcon />
      </Group>
    </Link>
  );
}

function NoRoleRow({ user }: { user: LegacyAtMeOutput | undefined }) {
  const { t } = useTranslation();
  return (
    <Group justify="between" className="w-full">
      <div className="grid gap-1 justify-items-start">
        <div className="text-lg">{t('redirect.noAccount.title')}</div>
        <div className="text-sm text-muted-foreground gap-2">
          {t('redirect.noAccount.helper')}
          <br />
          <Text inline weight="medium" className="break-all flex justify-center w-full">
            {user?.email}
          </Text>
        </div>
      </div>
    </Group>
  );
}

export function RedirectModule({ logo }: RedirectModuleProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { signOut } = useSignOut({
    onSuccess: () => {
      navigate('/auth/sign-in');
      clearCache();
      queryClient.clear();
      clearMfaSetupSkip();
    },
  });

  // Effect, not render: the take is one-shot and StrictMode renders twice
  useEffect(() => {
    const to = takeOAuthRedirect();
    if (to) navigate(to, { replace: true });
  }, []);

  const { data: user, status: getUserStatus } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const userProducts = user?.productIds ?? [];

  const businessRolesConfig = useKorintModuleConfig('businessRolesConfig');
  const relationTypes = [
    ...(user?.isSuperUser ? ['SUPER_ADMIN'] : []),
    ...(user?.brokerRole ? [user.brokerRole] : []),
    ...(user?.relations ?? []).map(({ relationType }) => relationType),
  ];
  // Some broker roles can't list distribution requests: fetch only when a redirect rule reads them
  const enableDistributionRequests =
    !!user?.brokerageFirmId &&
    relationTypes.some(relationType =>
      readsDistributionRequests(businessRolesConfig?.[relationType]?.redirectRules),
    );

  const {
    data: { data: distributionRequests } = {},
    status: distributionRequestsStatus,
    isLoading: isDistributionRequestsLoading,
  } = useQuery({
    ...viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions(
      user?.brokerageFirmId ?? '',
    ),
    enabled: enableDistributionRequests,
  });

  const brokerRelation = user?.brokerRole
    ? [
        {
          id: `${user.userId}-broker`,
          userId: user.userId,
          resourceId: user.brokerageFirmId ?? '',
          relationType: user.brokerRole,
          userProducts: userProducts,
          distributionRequests: distributionRequests,
        },
      ]
    : [];
  const superAdminRelation = user?.isSuperUser
    ? [
        {
          id: `${user.userId}-super_admin`,
          userId: user.userId,
          resourceId: user.brokerageFirmId ?? '',
          relationType: 'SUPER_ADMIN',
        },
      ]
    : [];

  const relations = [...superAdminRelation, ...brokerRelation].concat(user?.relations ?? []);

  const handleSignOut = () => {
    signOut();
  };

  return (
    <Stack align="center" justify="center" gap={0} className="min-h-screen">
      <div className="size-20 mb-6">{logo}</div>
      <Center className="w-full px-4">
        <Card className="w-full max-w-2xl">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle>{t('redirect.title')}</CardTitle>
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="size-4 mr-2" />
              {t('redirect.signOut')}
            </Button>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            {(getUserStatus === 'pending' || isDistributionRequestsLoading) && (
              <Loader className="size-8 animate-spin self-center" />
            )}
            {(getUserStatus === 'error' || distributionRequestsStatus === 'error') && (
              <ErrorMessage />
            )}
            {getUserStatus === 'success' &&
              (enableDistributionRequests ? distributionRequestsStatus === 'success' : true) &&
              relations.length >= 1 &&
              relations.map(({ id, relationType, resourceId }) => (
                <RoleRow
                  key={`${id}-${relationType}-${resourceId}`}
                  resourceId={resourceId}
                  relationType={relationType}
                  isUniqueRow={relations.length === 1}
                  userProducts={userProducts}
                  distributionRequests={distributionRequests}
                />
              ))}
            {getUserStatus === 'success' && relations.length === 0 && <NoRoleRow user={user} />}
          </CardContent>
        </Card>
      </Center>
    </Stack>
  );
}
